// @ts-strict-ignore
/**
 * Events menu: clickable list of events/custom events to go inside mp-drop-menu
 */
import * as React from 'react';
import sum from 'lodash/sum';
import clsx from 'clsx';

import {assertUnreachable} from 'common/util/typescript';
import {ContextPanelData} from 'common/widgets/context-panel/types';
import {LocalStorageKey, ResourceType} from 'common/report/constants';
import {getIconForEvent} from 'common/report/util/events';
import {debounce} from 'common/util/lodash-wrapped';
import {NUM_RECENT_ITEMS, TRACKING_EVENTS} from 'common/query-builder/query-block/constants';
import {
  ExpansionToggleItemClickEvent,
  ExpansionToggleLabelMode,
  ItemClickEvent,
  ItemFocusEvent,
  ItemsMenuSize,
  Section,
  SectionItem,
  SelectedTheme,
} from 'common/components/items-menu/types';
import {promptCustomEvents} from 'common/widgets/custom-event-modal/utils';
import {getProjectId, getWorkspaceId} from 'common/report/util';
import {captureWorkflowTiming} from 'common/util/workflow-timing';
import {TagInputChangeAction, TagInputChangeEvent, TagInputSearchMode} from 'common/components/tag-input/types';
import {TagItem} from 'common/components/tag-input/tag-item';
import {UserLocalStorageHelper} from 'common/report/user-local-storage';
import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';
import {TrackingUsageContext} from 'common/report/tracking-setup';
import {emitTrackEvent} from 'common/util/dom';
import {ButtonReact} from 'common/components/button';
import {ButtonSize, ButtonTheme} from 'common/components/button/constants';
import {ItemsMenuReact} from 'common/components/items-menu';
import {ResizablePanelReact} from 'common/components/resizable-panel';
import {Direction, PanelName} from 'common/components/resizable-panel/types';
import {SkeletonListReact} from 'common/components/skeleton-list';
import {SkeletonListRowSize} from 'common/components/skeleton-list/types';
import {TagInputReact} from 'common/components/tag-input';
import {Placement} from 'common/components/tooltip/types';
import {fuzzyMatch} from 'common/util/fuzzy-match';
import {ContextPanelReact} from 'common/widgets/context-panel';
import {IronContextRegistry} from 'common/report/context-registry';
import {useEventsController} from 'common/contexts/react/events-controller-context';
import {useLocalStorageManager} from 'common/contexts/react/storage-manager-context';
import {useTranslation} from 'common/i18n/hooks';

import {ChildMenuInterface, ChildMenuItemsCache, ContextPanelRenderer} from '../child-menu';
import {getCustomEventMenuItem} from '../contexts/events-controller';
import {getEventsMenuBodyHeight, getOpenedMenuComponent, OpenedMenuComponent} from '../utils/util';
import {template as noTagsTemplate} from './no-tags';
import {
  ActionType,
  Event,
  EventItem,
  EventMenuItem,
  ItemTriggerCustomEventModal,
  ModalType,
  SidebarCategory,
  SidebarItem,
  SidebarItemType,
} from './types';
import {
  filterToBookmarkableEventItem,
  getEventSelectedLabel,
  getEventsSections,
  getSearchSelectedTagItems,
  getSearchTagMenuSections,
  getTagsSection,
  isEventItem,
  sectionsFromInput,
} from './util';
import {createContextPanelEventHandlers} from './context-panel';

const USER_STORAGE_KEY = `default`;

interface EventsMenuState {
  eventData: EventItem[];
  includeHidden: boolean;
  searchFilter: string;

  /** state specific to improved QB menu */
  isSidebarTagSectionExpanded: boolean;
  selectedTagSet: Set<string>;

  // for multi-events-menu-only
  sections: Section[];
  isMenuLoading: boolean;
  stagedSelected: Set<any>;
}

export interface EventsMenuProps {
  isModalOpen?: boolean;
  onModalOpen?: () => void;
  onModalClose?: () => void;

  keydownProxyEl?: HTMLElement;
  onSetKeydownProxyEl?: (el: HTMLElement) => void;

  selected: any;
  tooltipData?: ContextPanelData;
  onTooltipChange?: (tooltipData: ContextPanelData) => void;

  otherOptions?: any;
  inverted?: boolean; // for multi-events-menu only
  includeHidden?: boolean; // for multi-events-menu only
  allDataGroups?: boolean;
  dataGroupId?: string;
  excludeAutotrackEvents?: boolean;
  excludeCustomEvents?: boolean;
  hideEditControls?: boolean;
  multiple?: boolean;
  open?: boolean;
  projectHasPeople?: boolean;
  readonly?: boolean;
  showAllEvents?: boolean;
  showAnyEvent?: boolean;
  showSessionEvent?: boolean;
  showSpecialDatasetProperty?: boolean;
  showTopEvents?: boolean;
  trackName?: string;

  eventNames?: string[];
  projectId?: number;
  userId?: number;
  workspaceId?: number;

  onSelect: (ev: any) => void;
  onDelete?: () => void;

  onRender?: () => void;
}

type PersistedEventsMenuState = Pick<EventsMenuState, `isSidebarTagSectionExpanded`>;

export class EventsMenu
  extends React.PureComponent<
    EventsMenuProps & Pick<IronContextRegistry, `eventsController` | `localStorageManager` | `i18n`>,
    EventsMenuState
  >
  implements ChildMenuInterface
{
  static contextPanelRenderer: ContextPanelRenderer = (params) => {
    if (!params.tooltipData) {
      return null;
    }

    return (
      <ContextPanelReact
        {...params.contextPanelProps}
        data={params.tooltipData}
        elref='contextPanel'
        {...createContextPanelEventHandlers({
          track: params.track,
          handleHoverIn: params.handleHoverIn,
          handleHoverOut: params.handleHoverOut,
          handleSelect: params.handleSelect,
          handleDelete: params.handleDelete,
          additionalArgs: params.additionalArgs,
        })}
      />
    );
  };
  private _openedAt: number = null;
  private _openCount = 0;
  private _numTotalItemsOnPrevRender: number = null;
  private declare _userLocalStorageHelper: UserLocalStorageHelper<PersistedEventsMenuState>;
  private _cache: ChildMenuItemsCache = null;

  protected childMenuRef = React.createRef<HTMLDivElement>();
  private eventsMenuBodyRef = React.createRef<HTMLDivElement>();
  private searchInputRef = React.createRef<HTMLInputElement>();

  // this is to support the workflow timers
  protected isConnected = false;
  private _onDisconnected = [];
  public onDisconnected(fn: () => void) {
    this._onDisconnected.push(fn);
  }

  static defaultProps = {
    otherOptions: [],
    isModalOpen: false,
    onModalOpen: () => null,
    onModalClose: () => null,
    tooltipData: null,
    onTooltipChange: () => null,
  };

  state = {
    eventData: [],
    includeHidden: false,
    searchFilter: ``,
    isSidebarTagSectionExpanded: false,
    selectedTagSet: new Set<string>(),
    sections: [],
    isMenuLoading: false,
    stagedSelected: new Set(),
  };

  constructor(props) {
    super(props);

    this._cache = ChildMenuItemsCache.createCache(
      props.userId || userCookieUtils.getUserIdentityFromCookie()?.id,
      Number(props.projectId || getProjectId()),
      `events-menu`,
      Number(props.workspaceId || getWorkspaceId()),
    );
  }

  get userLocalStorageHelper(): UserLocalStorageHelper<PersistedEventsMenuState> {
    if (!this._userLocalStorageHelper) {
      this._userLocalStorageHelper = new UserLocalStorageHelper({
        context: this.props.localStorageManager,
        namespace: LocalStorageKey.EventsMenu,
      });
    }
    return this._userLocalStorageHelper;
  }

  setPersistedState(): void {
    this.userLocalStorageHelper.setItem({
      userId: userCookieUtils.getUserIdentityFromCookie()?.id,
      key: USER_STORAGE_KEY,
      value: {isSidebarTagSectionExpanded: this.state.isSidebarTagSectionExpanded},
    });
  }

  componentDidMount() {
    this.isConnected = true;
    this.setOpenedAt(this.props.open);

    const persistedState = this.userLocalStorageHelper.getItem({
      key: USER_STORAGE_KEY,
      userId: userCookieUtils.getUserIdentityFromCookie()?.id,
    });
    if (persistedState) {
      this.setState(persistedState);
    }
  }

  componentWillUnmount() {
    this.isConnected = false;
    this._onDisconnected.forEach((fn) => fn());
  }

  getRecentEvents(limit = null) {
    return this._cache.getRecentItems(ResourceType.Event, limit);
  }

  addRecentEvent(item) {
    this._cache.addRecentItem(ResourceType.Event, item, (cmpItem) => cmpItem.value);
  }

  /**
   * See comment for analogous functionality in <mp-properties-menu>
   */
  setOpenedAt(open: boolean) {
    // setOpenedAt may be called before this.attr managed state is available.
    if (open) {
      if (!this._openedAt) {
        this._openedAt = new Date().getTime();
        this._openCount += 1;

        let openedComponent: OpenedMenuComponent;
        captureWorkflowTiming(this, {
          name: `Events Menu`,
          key: `events-menu`,
          isComplete: (ev) => {
            if (!this.childMenuRef.current) {
              return false;
            }
            openedComponent = getOpenedMenuComponent(ev, this.childMenuRef.current);
            return Boolean(openedComponent);
          },
          additionalProps: () => ({
            openCount: this._openCount,
            openedComponent,
          }),
        });
        captureWorkflowTiming(this, {
          name: `Events Menu Loaded`,
          key: `events-menu-loaded`,
          isComplete: (ev) => {
            if (!this.childMenuRef.current) {
              return false;
            }
            return getOpenedMenuComponent(ev, this.childMenuRef.current) === OpenedMenuComponent.ItemsMenu;
          },
          additionalProps: () => ({
            numTotalItems: this._numTotalItemsOnPrevRender,
            openCount: this._openCount,
          }),
        });
      }
    } else {
      this._openedAt = null;
    }
  }

  componentDidUpdate(prevProps: Readonly<EventsMenuProps>): void {
    if (this.props !== prevProps) {
      this.setOpenedAt(this.props.open);
    }

    if (this.props.open !== prevProps.open) {
      this.setState({
        includeHidden: false,
        selectedTagSet: new Set(),
        searchFilter: ``,
      });

      if (this.props.open) {
        this.trackEvent(TRACKING_EVENTS.OPEN_EVENTS_DROPDOWN);
      }
    }

    this.props.onRender?.();
  }

  getMenuSections() {
    const sections = this.getSections();
    this._numTotalItemsOnPrevRender = sum(sections.map((section) => section.items.length));
    return sections;
  }

  isLoading() {
    return this.props.eventsController.loading;
  }

  canCreateItems(): boolean {
    return (
      !this.props.excludeCustomEvents &&
      !this.props.hideEditControls &&
      this.props.eventsController.canWriteCustomEvents()
    );
  }

  // This is for the Create Custom button at the bottom of the menu
  triggerCreateCustomEventModal() {
    return this.triggerModal(ModalType.CreateCustomEvent);
  }

  public trackEvent(eventName, props = {}, {cancelable = false} = {}) {
    const additionalProps = {
      [`usage context`]: TrackingUsageContext.Report,
    };
    if (this.props.trackName) {
      additionalProps[`tab`] = this.props.trackName;
    }
    emitTrackEvent(this.childMenuRef.current, {
      props: {
        ...props,
        ...additionalProps,
      },
      eventName,
      cancelable,
    });
  }

  public trackSelection({props, ...eventData}, cancelable = true) {
    const additionalProps = {
      [`usage context`]: TrackingUsageContext.Report,
      'Event Names': this.props.eventNames,
    };
    if (this.props.trackName) {
      additionalProps[`tab`] = this.props.trackName;
    }
    emitTrackEvent(this.childMenuRef.current, {
      ...eventData,
      props: {
        ...props,
        ...additionalProps,
      },
      cancelable,
    });
  }

  handleTooltipContextAction() {
    return null;
  }

  handleCustomEventDelete(deletedId: number) {
    if (deletedId && this.props.selected?.id === deletedId) {
      // if deleting the selected custom event we delete the entire block
      this.props.onDelete?.();
    }
  }

  canOpenMenu() {
    return !this.props.readonly;
  }

  shouldCloseOnSelect() {
    return true;
  }

  handleCloseMenu() {
    this.updateContextPanel.cancel();
  }

  handleItemFocus({detail}: ItemFocusEvent<EventItem>) {
    this.updateContextPanel(detail.item.value);
  }

  async triggerModal(modalType: ModalType): Promise<void> {
    if (modalType === ModalType.CreateCustomEvent) {
      this.props.onModalOpen();
      const {appliedValue} = await promptCustomEvents({
        open: true,
        'all-data-groups': this.props.allDataGroups,
        'data-group-id': this.props.dataGroupId ?? ``,
        'project-has-people': this.props.projectHasPeople,
        'project-id': getProjectId(),
        'workspace-id': getWorkspaceId(),
        'track-name': this.props.trackName,
      });

      if (appliedValue) {
        const eventItem = getCustomEventMenuItem(appliedValue).value;
        this.selectItem(eventItem);
      }
      this.props.onModalClose();
    }
  }

  selectItem(
    rawItem: EventItem,
    {searchFilter = ``, labelMatch = {}, subLabelMatch = {}, customMatch = {}, topScrollPosition = 0} = {},
  ) {
    const item = filterToBookmarkableEventItem(rawItem);

    this.addRecentEvent(item);

    const trackingProps = this.trackingProps(
      item,
      searchFilter,
      labelMatch,
      subLabelMatch,
      customMatch,
      topScrollPosition,
    );
    this.trackSelection(trackingProps);
    this.props.onSelect({detail: {item}});
  }

  handleClickItem({detail}: ItemClickEvent<EventMenuItem>) {
    const {item} = detail;
    switch (item.value.action) {
      case ActionType.TriggerModal:
        this.triggerModal(item.value.modalType);
        break;
      case ActionType.Refresh:
        this.refreshEvents();
        break;
      case ActionType.Select:
      case undefined:
        this.selectItem(item.value, detail);
        break;
      default:
        assertUnreachable(item.value);
    }
  }

  getTooltipData(item: EventItem): ContextPanelData {
    const tooltipData = this.props.eventsController.getContextPanelData(item, {
      allowEdit: !this.props.readonly && !this.props.hideEditControls,
    });

    return tooltipData;
  }

  private handleSearchInput(searchFilter) {
    const isStartingSearch = Boolean(searchFilter && !this.state.searchFilter);
    const isClearingSearch = Boolean(this.state.searchFilter && !searchFilter);
    this.setState({searchFilter});
    if (isStartingSearch) {
      this.trackEvent(TRACKING_EVENTS.SEARCH_STARTED);
    } else if (isClearingSearch) {
      this.trackEvent(TRACKING_EVENTS.SEARCH_CLEARED);
    }
  }

  private debouncedSearchInput = debounce((searchInput) => this.handleSearchInput(searchInput), 200);

  private updateContextPanel = debounce(
    (focusedItem) => {
      this.props.onTooltipChange(this.getTooltipData(focusedItem));
    },
    250,
    {leading: true},
  );

  getSelectedHoverTooltipData(): ContextPanelData {
    return this.props.selected && this.props.selected.value ? this.getTooltipData(this.props.selected) : null;
  }

  getSelectedTrackingProps(): Obj {
    if (!this.props.selected) {
      return null;
    }

    const props = {'Entity type': `event`};
    if (this.props.selected.custom) {
      props[`Is custom`] = true;
    }
    return props;
  }

  getSections() {
    return getEventsSections({
      recentlyUsedEvents: this.getRecentEvents(NUM_RECENT_ITEMS),
      eventsController: this.props.eventsController,
      dataGroupId: this.props.dataGroupId,
      excludeAutotrackEvents: this.props.excludeAutotrackEvents,
      excludeCustomEvents: this.props.excludeCustomEvents,
      searchFilter: this.state.searchFilter,
      selectedTagSet: this.state.selectedTagSet,
      selected: this.props.selected,
      showAllEvents: this.props.showAllEvents,
      showAnyEvent: this.props.showAnyEvent,
      showTopEvents: this.props.showTopEvents,
      showSessionEvent: this.props.showSessionEvent,
      showSpecialDatasetProperty: this.props.showSpecialDatasetProperty,
      otherOptions: this.props.otherOptions,
    });
  }

  getRecentsSection(): Section<EventItem> {
    const recentsSection = this.props.eventsController.getRecentsSection(
      this.getRecentEvents(NUM_RECENT_ITEMS) as EventItem[],
    );
    return {
      ...recentsSection,
      items: recentsSection.items.filter((eventItem) => {
        return Boolean(
          Array.from(this.state.selectedTagSet).every((tagFilter) => {
            return Boolean(eventItem.value.tags?.includes(tagFilter));
          }),
        );
      }),
    };
  }

  selectedLeftIcon() {
    return getIconForEvent(this.props.selected);
  }

  selectedLabel() {
    return getEventSelectedLabel(this.props.selected as Event, {
      includeNameOnRename: true,
      labelPrefix: this.props.selected?.labelPrefix,
    });
  }

  trackingProps(selected, searchFilter, labelMatch, subLabelMatch, customMatch, topscrollPosition) {
    const extraData = this.getTooltipData(selected);
    const extraEventTrackingProps = {
      'num of images': extraData.imageInfo?.length,
    };

    return {
      eventName: `[mp-query-builder] Select event`,
      props: {
        'Event name': selected.label,
        'top scroll': topscrollPosition,
        'is custom event': !!selected.custom,
        'is autotrack event': !!selected.isCollectEverything,
        'is hidden entity': !!selected.hidden,
        'is recently viewed': !!selected.isRecentlyUsed,
        'Search - filter': searchFilter,
        'Search - label match': labelMatch,
        'Search - sublabel match': subLabelMatch,
        'Search - custom match': customMatch,
        'Metadata Search Enabled': true,
        'Elapsed time (ms) since menu opened': new Date().getTime() - this._openedAt,
        ...extraEventTrackingProps,
      },
    };
  }

  refreshEvents(): void {
    this.props.eventsController.refreshData();
    this.trackEvent(TRACKING_EVENTS.REFRESHED_EVENTS);
  }

  //#region improved QB menu specific code

  getSearchSelectedTagItems(): TagItem[] {
    return getSearchSelectedTagItems({
      selectedTagSet: this.state.selectedTagSet,
      i18n: this.props.i18n,
      eventsController: this.props.eventsController,
    });
  }

  deleteTag(tag: string): void {
    const updatedSelectedTagSet = new Set(this.state.selectedTagSet);
    updatedSelectedTagSet.delete(tag);
    this.setState({selectedTagSet: updatedSelectedTagSet});
  }

  addTag(tag: string): void {
    const updatedSelectedTagSet = new Set(this.state.selectedTagSet);
    updatedSelectedTagSet.add(tag);
    this.setState({selectedTagSet: updatedSelectedTagSet});
  }

  getSearchTagMenuSections(): Array<Section<TagItem>> {
    return getSearchTagMenuSections({
      selectedTagSet: this.state.selectedTagSet,
      i18n: this.props.i18n,
      eventsController: this.props.eventsController,
    });
  }

  handleSearchTagMenuItemClick(ev: ItemClickEvent<TagItem>) {
    this.addTag(ev.detail.item.value.value);
  }

  handleSearchInputChange(ev: TagInputChangeEvent): void {
    const {detail} = ev;
    if (detail.action === TagInputChangeAction.Text) {
      this.debouncedSearchInput(detail.value);
    } else if (detail.action === TagInputChangeAction.RemoveTag) {
      this.deleteTag(detail.tag.value);
    }
  }

  handleSearchInputClear(): void {
    this.setState({
      searchFilter: ``,
      selectedTagSet: new Set(),
    });
  }

  getSidebarSections(): Array<Section<SidebarItem>> {
    const {t} = this.props.i18n;
    const tagsSection = getTagsSection(this.props.eventsController);

    const sidebarTagSection: Section<SidebarItem> = {
      icon: `tag`,
      label: t(`query-builder.events-menu.sidebar-tags`),
      selectedTheme: SelectedTheme.Checkmark,
      expansionToggle: {
        isExpanded: this.state.isSidebarTagSectionExpanded,
        labelMode: ExpansionToggleLabelMode.Unchanged,
        alwaysShow: true,
      },
      items: tagsSection.items.map((tag): SectionItem<SidebarItem> => {
        return {
          label: tag.label,
          isSelected: this.state.selectedTagSet.has(tag.value.value),
          value: {
            type: SidebarItemType.EventTag,
            tag: tag.value.value,
          },
        };
      }),
      emptySectionRenderer: noTagsTemplate,
    };

    return [
      {
        selectedTheme: SelectedTheme.Focus,
        items: [
          {
            label: t(`query-builder.events-menu.sidebar-all`),
            icon: `event`,
            isSelected: true,
            value: {
              type: SidebarItemType.Category,
              category: SidebarCategory.All,
            },
          },
        ],
      },
      sidebarTagSection,
    ];
  }

  handleClickSidebarItem({detail}: ItemClickEvent<SidebarItem>): void {
    const itemValue = detail.item.value;
    if (itemValue.type === SidebarItemType.EventTag) {
      const {tag} = itemValue;
      if (this.state.selectedTagSet.has(tag)) {
        this.deleteTag(tag);
      } else {
        this.addTag(tag);
      }
    }
    this.searchInputRef.current.focus();
  }

  handleClickSidebarExpansionToggleItem({detail}: ExpansionToggleItemClickEvent): void {
    this.setState({isSidebarTagSectionExpanded: detail.isExpanded});
    this.setPersistedState();
  }

  //#endregion

  render() {
    const {t} = this.props.i18n;

    const contextPanelEventHandlers = createContextPanelEventHandlers({
      track: this.trackEvent.bind(this),
      handleSelect: ({item}: {item: SectionItem<ItemTriggerCustomEventModal>}) =>
        this.triggerModal(item.value.modalType),
      handleDelete: ({id}) => this.handleCustomEventDelete(id),
      additionalArgs: {
        'all-data-groups': this.props.allDataGroups,
        'project-has-people': this.props.projectHasPeople,
        'track-name': this.props.trackName,
      },
    });
    return (
      <div className={clsx(`mp-events-menu`, `improved-menu`, `mp-child-menu`)} ref={this.childMenuRef}>
        {this.props.open && (
          <>
            <div className='search-wrapper'>
              <TagInputReact
                ref={this.searchInputRef as React.Ref<any>}
                className='search-input'
                tags={this.getSearchSelectedTagItems()}
                placeholder={t(`query-builder.events-menu.search-placeholder`)}
                minTags={0}
                menuWidth={180}
                minInputWidth={150}
                icon={`search`}
                showClear
                searchMode={TagInputSearchMode.Hashtag}
                mpItemsMenuAttrs={{
                  'empty-message': t(`query-builder.events-menu.no-tags`),
                  'empty-search-results-message': t(`query-builder.no-search-results`),
                }}
                controlledMode
                value={this.state.searchFilter}
                menuSections={this.getSearchTagMenuSections()}
                onChange={(ev) => this.handleSearchInputChange(ev)}
                onClickMenuItem={(ev) => this.handleSearchTagMenuItemClick(ev)}
                onClear={() => this.handleSearchInputClear()}
              />
              {this.canCreateItems() && (
                <ButtonReact
                  className='create-custom-button'
                  icon={`plus`}
                  theme={ButtonTheme.Tertiary}
                  size={ButtonSize.Medium}
                  tooltip={{text: t(`query-builder.events-menu.create-tooltip`), placement: Placement.Top}}
                  onClick={async () => this.triggerCreateCustomEventModal()}
                >
                  {t(`query-builder.create`)}
                </ButtonReact>
              )}
            </div>
            <div ref={this.eventsMenuBodyRef} className='events-menu-body' style={{height: getEventsMenuBodyHeight()}}>
              <ResizablePanelReact
                className='sidebar-panel'
                storageKey={`events-menu-sidebar`}
                defaultSize={130}
                minSize={130}
                maxSize={300}
                disableCollapsing
                panelName={PanelName.Events}
                defaultAlignment={Direction.Left}
                containerEl={this.eventsMenuBodyRef.current}
              >
                {this.isLoading() ? (
                  <SkeletonListReact
                    className={clsx(`sidebar`, `sidebar-skeleton-list`)}
                    numRows={10}
                    rowSize={SkeletonListRowSize.Large}
                  />
                ) : (
                  <ItemsMenuReact
                    className='sidebar'
                    // tabindex={0}
                    size={ItemsMenuSize.Small}
                    disableFirstItemPadding
                    sections={this.getSidebarSections()}
                    onClickItem={(ev) => this.handleClickSidebarItem(ev)}
                    onClickExpansionToggleItem={(ev) => this.handleClickSidebarExpansionToggleItem(ev)}
                  />
                )}
              </ResizablePanelReact>
              {this.isLoading() ? (
                <SkeletonListReact
                  numRows={10}
                  rowSize={SkeletonListRowSize.Large}
                  className='mp-events-menu-skeleton-list'
                />
              ) : (
                <ItemsMenuReact
                  className='mp-events-menu-items-menu'
                  elref={`mainSelectionMenu`}
                  emptyMessage={t(`query-builder.no-items`)}
                  multiple={this.props.multiple}
                  searchFilter={this.state.searchFilter}
                  disableSearchSort
                  disableFirstItemPadding
                  onClick={(ev) => ev.stopPropagation()}
                  onClickItem={(ev) => this.handleClickItem(ev)}
                  onHoverIn={contextPanelEventHandlers.onHoverIn}
                  onHoverOut={contextPanelEventHandlers.onHoverOut}
                  onTooltipContextAction={contextPanelEventHandlers.onTooltipContextAction}
                  onTooltipContextEditDescription={contextPanelEventHandlers.onTooltipContextEditDescription}
                  sections={this.getMenuSections()}
                  keydownProxyEl={!this.props.isModalOpen && this.searchInputRef.current}
                  sectionsFromInputFn={(searchFilter, sections) =>
                    sectionsFromInput({searchFilter, sections, i18n: this.props.i18n})
                  }
                  customSearchMatcherFn={(item, searchString) => fuzzyMatch(item.searchableContent, searchString)}
                  // Todo: Move ContextPanel from items-menu to a custom widget
                  contextPanelTransform={(sectionItem: SectionItem<EventMenuItem>) => {
                    const eventMenuItem = sectionItem.value;
                    if (!eventMenuItem || !isEventItem(eventMenuItem)) {
                      return null;
                    }
                    if (eventMenuItem && !eventMenuItem.icon) {
                      eventMenuItem.icon = sectionItem.icon;
                    }
                    if (sectionItem.itemFromInput) {
                      eventMenuItem.itemFromInput = sectionItem.itemFromInput;
                    }
                    return this.getTooltipData(eventMenuItem);
                  }}
                  contextPanelPositionParentElement={this.childMenuRef.current}
                />
              )}
            </div>
          </>
        )}
      </div>
    );
  }
}

export const EventsMenuReact = React.forwardRef<EventsMenu, EventsMenuProps>((props, ref) => {
  const eventsController = useEventsController();
  const localStorageManager = useLocalStorageManager();
  const i18n = useTranslation();

  return (
    <EventsMenu
      ref={ref}
      eventsController={eventsController}
      localStorageManager={localStorageManager}
      i18n={i18n}
      {...props}
    />
  );
});
